import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loadGsap, prefersReducedMotion } from '../lib/gsap';

// About keeps native scrolling — CSS sticky on the story image breaks under ScrollSmoother transforms.
const NATIVE_SCROLL_ROUTES = new Set(['/about']);

function SmoothScroll({ children }) {
  const location = useLocation();
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const smootherRef = useRef(null);

  useEffect(() => {
    if (NATIVE_SCROLL_ROUTES.has(location.pathname) || prefersReducedMotion()) {
      return undefined;
    }

    let cancelled = false;
    let smoother;

    (async () => {
      const { ScrollSmoother, ScrollTrigger } = await loadGsap();
      if (cancelled || !wrapperRef.current || !contentRef.current) return;

      ScrollSmoother.get()?.kill();
      smoother = ScrollSmoother.create({
        wrapper: wrapperRef.current,
        content: contentRef.current,
        smooth: 1.15,
        smoothTouch: 0.12,
        effects: true,
        normalizeScroll: true,
        ignoreMobileResize: true,
      });
      smootherRef.current = smoother;

      smoother.scrollTo(0, false);
      document.documentElement.classList.add('has-gsap-smooth-scroll');
      ScrollTrigger.refresh();
    })();

    return () => {
      cancelled = true;
      smoother?.kill();
      smootherRef.current = null;
      document.documentElement.classList.remove('has-gsap-smooth-scroll');
    };
  }, [location.pathname]);

  const handleClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    const target = document.querySelector(hash);
    const smoother = smootherRef.current;
    if (!target || !smoother) return;

    event.preventDefault();
    smoother.scrollTo(target, true, 'top top');
    window.history.replaceState(null, '', hash);
  };

  return (
    <div id="smooth-wrapper" ref={wrapperRef}>
      <div id="smooth-content" ref={contentRef} onClick={handleClick}>
        {children}
      </div>
    </div>
  );
}

export default SmoothScroll;
